import React from "react";
import { useQuery } from "@apollo/client";
import { Link, Redirect } from "react-router-dom";
import { Alert, BreadcrumbItem, Card, CardBody, Col, Container, Row } from "reactstrap";
import * as Loading from "@repoflow/components/loading";
import * as EntryQL from "queries/entry";
import * as ProfileQL from "queries/profile";
import { addDays, dayLabel, isDay, today, weekdayShort } from "components/day/dates";
import { byMeal, formatNumber, macroSplit, progress } from "components/day/summary";
import { useMeals } from "components/settings/meals";
import "components/day/day.css";

export const Sidebar = () => null;

export const Indice = ({ match: { params: { date } } }) => (
  <BreadcrumbItem>{isDay(date) ? dayLabel(date) : date}</BreadcrumbItem>
);

// How many days the strip across the top shows, ending on the one on screen.
const WEEK = 7;

// One day of the food log: a week strip to move between days, the day's
// totals, and what was eaten, by meal.
//
// ONE read for all of it. `days` returns every day of the range with its totals
// and its entries, so the strip and the list below are the same response and
// cannot disagree about what was logged. `cache-and-network` because the strip
// is shared between seven neighbouring screens: moving a day back shows the
// cached week at once and corrects it when the network answers.
export const Content = ({ match: { params: { date } } }) => {
  const valid = isDay(date);
  const from = valid ? addDays(date, -(WEEK - 1)) : null;

  const { data, loading, error } = useQuery(EntryQL.days, {
    variables: { input: { from, to: date } },
    skip: !valid,
    fetchPolicy: "cache-and-network",
  });
  const targets = ProfileQL.useTargets();
  const target = targets.calories;

  // A typo'd or impossible day (`/day/2026-02-31`) is not an error worth a
  // screen: there is an obvious day the person meant to land on.
  if (!valid) return <Redirect to={`/day/${today()}`} />;

  const days = data?.viewer?.account?.entries?.days || [];
  const current = days.find((d) => d.date === date);

  return (
    <Container className="noshchat-day mt-4">
      <DayNav date={date} />

      {error ? (
        <Alert color={"danger"} className="mt-3">
          The food log could not be read. {error.message}
        </Alert>
      ) : null}

      {!data && loading ? (
        <div className="mt-4"><Loading.Icon /></div>
      ) : (
        <>
          <WeekStrip days={days} date={date} target={target} />
          <Totals totals={current?.totals} target={target} macros={targets} />
          <Meals date={date} entries={current?.entries || []} />
        </>
      )}
    </Container>
  );
};

// Two rows on a phone, one from `md` up: the title gets the whole width it
// needs and the buttons sit under it, rather than the four of them squeezing
// "Wednesday, October 21" into a column a word wide.
const DayNav = ({ date }) => (
  <Row className="align-items-center">
    <Col xs={"auto"}>
      <Link className="btn btn-outline-secondary btn-sm" to={`/day/${addDays(date, -1)}`} aria-label={"Previous day"}>
        <i className="fa fa-chevron-left" />
      </Link>
    </Col>
    <Col>
      <h4 className="m-0" data-testid={"day-title"}>{dayLabel(date)}</h4>
      <div className="text-muted small">{date}</div>
    </Col>
    <Col xs={12} md={"auto"} className="mt-2 mt-md-0 text-nowrap">
      {date !== today() ? (
        <Link className="btn btn-outline-secondary btn-sm mr-2" to={`/day/${today()}`}>
          Today
        </Link>
      ) : null}
      <Link className="btn btn-outline-secondary btn-sm mr-2" to={`/day/${addDays(date, 1)}`} aria-label={"Next day"}>
        <i className="fa fa-chevron-right" />
      </Link>
      <Link className="btn btn-primary btn-sm" to={`/day/${date}/new`}>
        <i className="fa fa-plus" /> Add food
      </Link>
    </Col>
  </Row>
);

// Seven days, the last of them the one on screen, each a link to itself with
// its calories under it. With a daily target the bars are measured against
// it, and a line across each bar marks it; the scale is the target or the
// busiest day, whichever is higher, so a day over the target still fits.
// Without one the bars are each day's share of the busiest day in the strip.
const WeekStrip = ({ days, date, target = null }) => {
  const max = Math.max(1, target || 0, ...days.map((d) => d.totals.calories));

  return (
    <div className="noshchat-week mt-3" role={"list"} aria-label={"The last seven days"}>
      {days.map((d) => (
        <Link
          key={d.date}
          to={`/day/${d.date}`}
          role={"listitem"}
          className={`noshchat-week__day${d.date === date ? " noshchat-week__day--current" : ""}`}
          aria-current={d.date === date ? "date" : undefined}
        >
          <span className="noshchat-week__label">{weekdayShort(d.date)}</span>
          <span className="noshchat-week__bar">
            <span
              className={target && d.totals.calories > target ? "noshchat-week__fill--over" : undefined}
              style={{ height: `${Math.round((d.totals.calories / max) * 100)}%` }}
            />
            {target ? (
              <i className="noshchat-week__target" style={{ bottom: `${Math.round((target / max) * 100)}%` }} />
            ) : null}
          </span>
          <span className="noshchat-week__kcal">{formatNumber(d.totals.calories)}</span>
        </Link>
      ))}
    </div>
  );
};

// `compact` stacks the calories over the macros at every width, and the macros
// two by two: the chat's side panel is a column, whatever the screen is. `target` is the daily
// target in kcal, or null when none is set — then the card says where to set
// one instead of how much is left. `macros` holds the macro targets in grams
// (ProfileQL.useTargets), each null when unset: a macro with one is measured
// against it, one without shows its share of the energy as before. Fibre has
// no target, so it always shows its share.
export const Totals = ({ totals, compact = false, target = null, macros = {} }) => {
  const t = totals || { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0, count: 0 };
  const split = macroSplit(t);
  const goal = progress(t.calories, target);

  return (
    <Card className="mt-3">
      <CardBody>
        <Row className="align-items-center">
          <Col xs={12} md={compact ? 12 : 4}>
            <div className="noshchat-total__kcal" data-testid={"day-calories"}>
              {formatNumber(t.calories)}
            </div>
            <div className="text-muted">kcal eaten</div>
            <TargetLine goal={goal} />
          </Col>
          <Col xs={12} md={compact ? 12 : 8} className={compact ? "mt-3" : undefined}>
            <Row>
              <Macro label={"Protein"} grams={t.protein} share={split.protein} kind={"protein"} target={macros.protein} compact={compact} />
              <Macro label={"Carbs"} grams={t.carbs} share={split.carbs} kind={"carbs"} target={macros.carbs} compact={compact} />
              <Macro label={"Fat"} grams={t.fat} share={split.fat} kind={"fat"} target={macros.fat} compact={compact} />
              <Macro label={"Fiber"} grams={t.fiber} share={split.fiber} kind={"fiber"} compact={compact} />
            </Row>
            <div className="noshchat-split mt-2" aria-hidden={"true"}>
              <span className="noshchat-split--protein" style={{ width: `${split.protein}%` }} />
              <span className="noshchat-split--carbs" style={{ width: `${split.carbs}%` }} />
              <span className="noshchat-split--fat" style={{ width: `${split.fat}%` }} />
              <span className="noshchat-split--fiber" style={{ width: `${split.fiber}%` }} />
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const TargetLine = ({ goal }) =>
  goal ? (
    <div className="mt-2" data-testid={"day-target"}>
      <div className="small">
        of <strong data-testid={"day-target-kcal"}>{formatNumber(goal.target)}</strong> kcal target ·{" "}
        {goal.over ? (
          <span className="noshchat-target--over" data-testid={"day-target-over"}>{formatNumber(goal.over)} over</span>
        ) : (
          <span data-testid={"day-target-left"}>{formatNumber(goal.left)} left</span>
        )}
      </div>
      <div
        className="noshchat-target__bar mt-1"
        role={"progressbar"}
        aria-label={"Calories eaten of the daily target"}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={goal.percent}
      >
        <span className={goal.over ? "noshchat-target__fill--over" : undefined} style={{ width: `${goal.percent}%` }} />
      </div>
    </div>
  ) : (
    <div className="small mt-2" data-testid={"day-target-unset"}>
      <Link to={`/target`}>Set a daily target</Link>
    </div>
  );

// Two by two on a phone and in the side panel, where a quarter of the width is
// narrower than "100.5 g"; four across otherwise.
const Macro = ({ label, grams, share, kind, target = null, compact }) => {
  const goal = progress(grams, target);
  return (
    <Col xs={6} sm={compact ? 6 : 3}>
      <div className="small text-muted">
        <span className={`noshchat-dot noshchat-dot--${kind}`} /> {label}
      </div>
      <div className="noshchat-total__macro" data-testid={`day-${kind}`}>
        {formatNumber(grams, 1)} g
      </div>
      {goal ? (
        <div data-testid={`day-${kind}-target`}>
          <div className="small text-muted">
            of {formatNumber(goal.target)} g ·{" "}
            {goal.over ? (
              <span className="noshchat-target--over" data-testid={`day-${kind}-over`}>{formatNumber(goal.over)} over</span>
            ) : (
              <span data-testid={`day-${kind}-left`}>{formatNumber(goal.left)} left</span>
            )}
          </div>
          <div
            className="noshchat-target__bar noshchat-target__bar--macro mt-1"
            role={"progressbar"}
            aria-label={`${label} eaten of its target`}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={goal.percent}
          >
            <span className={goal.over ? "noshchat-target__fill--over" : `noshchat-target__fill--${kind}`} style={{ width: `${goal.percent}%` }} />
          </div>
        </div>
      ) : (
        <div className="small text-muted">{share}% of energy</div>
      )}
    </Col>
  );
};

// The meals the settings show (see byMeal for the one exception), each a card.
// Held back until the settings are read, rather than drawing all four and
// taking some away a moment later. The chat's side panel shows the same
// groups as tabs instead (routes/chat MealTabs).
export const Meals = ({ date, entries }) => {
  const shown = useMeals();
  if (!shown) return null;

  return (
    <>
      {byMeal(entries, shown).map((group) => (
        <Card className="mt-3" key={group.meal} data-testid={`meal-${group.meal}`}>
          <CardBody>
            <Row className="align-items-center">
              <Col className="text-nowrap">
                <h5 className="m-0">{group.label}</h5>
              </Col>
              <Col xs={"auto"} className="text-muted px-0">
                {formatNumber(group.calories)} kcal
              </Col>
              <Col xs={"auto"}>
                <AddLink date={date} group={group} />
              </Col>
            </Row>
            {group.items.length ? <EntryList items={group.items} className="mt-2" /> : null}
          </CardBody>
        </Card>
      ))}
    </>
  );
};

// A hidden meal is only ever listed because the day already has something at
// it, and nothing new goes there, so it has no Add.
export const AddLink = ({ date, group }) =>
  group.hidden ? null : (
    <Link className="btn btn-link btn-sm" to={`/day/${date}/new?meal=${group.meal}`}>
      <i className="fa fa-plus" /> Add
    </Link>
  );

// One meal's entries, each a link to edit it.
export const EntryList = ({ items, className = "" }) => (
  <ul className={`list-unstyled m-0 noshchat-meal__entries ${className}`}>
    {items.map((entry) => (
      <li key={entry.entryId} className="noshchat-entry">
        <Link to={`/entry/${entry.entryId}`} className="noshchat-entry__link">
          <span className="noshchat-entry__food">
            {entry.food}
            {entry.serving ? <span className="text-muted"> · {entry.serving}</span> : null}
          </span>
          <span className="noshchat-entry__macros text-muted small">
            P {formatNumber(entry.protein, 1)} · C {formatNumber(entry.carbs, 1)} · F {formatNumber(entry.fat, 1)} · Fiber{" "}
            {formatNumber(entry.fiber, 1)}
          </span>
          <span className="noshchat-entry__kcal">{formatNumber(entry.calories)} kcal</span>
        </Link>
      </li>
    ))}
  </ul>
);
